import "../css/primariesPage.scss";
import React, { useContext, useState, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";

import { timesheetsRef, restoresRef, quotesRef, jobsheetsRef } from "../firebase";

import { tooltips } from "../popData";

import { transferTimesheets, cloneTimesheets } from "../helperFunctions/dbFunctions";

import {
  Edit as EditIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";

import {
  AuthContext,
  SettingsContext,
  JobsheetsContext,
  SlidersContext,
  TimesheetsContext,
  QuotesContext,
} from "../providers";

import {
  Footer,
  FooterBtn,
  SliderWrapper,
  SliderContainer,
  StatusFilters,
  CloneOptions,
  Loading,
  SearchSortFilterBar,
  PrimarySection,
} from "../components";

import {
  useEditMode,
  useGlobalSearchData,
  useSliderControl,
  useViewWindowExtender,
  useSectionedData,
  useAddPrimary,
  useBatchPendingDraftsCheck,
} from "../customHooks";
import { defaultOptions } from "../helperFunctions/sortAndFilterFunctions";

const Timesheets = () => {
  const auth = useContext(AuthContext);

  // Sliders >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  const containerRef = useRef(null);
  const { sliders, setSliderId, sliderId } = useContext(SlidersContext);

  // Determines how slider should react in different scenarios
  useSliderControl(sliders, setSliderId);
  // Sliders <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Source of primaries data >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  const { timesheets, loading } = useContext(TimesheetsContext);
  const { timesheets: jobsheets } = useContext(JobsheetsContext);
  const { timesheets: quotes } = useContext(QuotesContext);
  // Source of primaries data <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Sort and filter options for page >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  const [sortAndFilterOptions, setSortAndFilterOptions] = useState(() => defaultOptions("timesheet"));
  // Sort and filter options for page <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Setup/dependencies for various functions defined in component >>>>>>>>>>>>>>>>>
  const history = useHistory();
  const addPrimary = useAddPrimary();

  const {
    currentQuoteNumber,
    currentJobsheetNumber,
    incCurrentPrimaryNumber,
    vat,
    // currentTsNumber,
  } = useContext(SettingsContext);

  const { inEditMode, toggleEditMode, isMarked, markMonth, markItem, markedItems, editMarkedItems } =
    useEditMode();
  // Setup/dependencies for various functions defined in component <<<<<<<<<<<<<<<<<

  // Format / Sort / Filter Data >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

  // Primary data for page
  const sectionedTimesheets = useSectionedData(timesheets, sortAndFilterOptions);

  // Opportunistically resolve any pending Gmail drafts on the most recent timesheets
  // (mirrors the display's own default sort - newest refNumber first) whenever this page
  // is opened, rather than only when someone reopens that specific timesheet.
  const recentTimesheets = [...timesheets]
    .sort((a, b) => Number(b.refNumber) - Number(a.refNumber))
    .slice(0, 10);
  useBatchPendingDraftsCheck(recentTimesheets);

  // Global search data
  let isSearching = sortAndFilterOptions?.searchTerm.length > 0;
  const globalSearchData = useGlobalSearchData(jobsheets, quotes, sortAndFilterOptions, isSearching);

  let showGlobalSearch = isSearching && globalSearchData.length > 0;
  // Format / Sort / Filter data <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Enables infinite scroll like behaviour >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  const [endOfViewWindow] = useViewWindowExtender(
    timesheets,
    sortAndFilterOptions,
    setSortAndFilterOptions,
    sectionedTimesheets,
    sliderId,
  );
  // Enable infinite scroll like behaviour <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Footer setup >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  let showFooter = true;
  let showBin = inEditMode && markedItems.length > 0;

  if (sliders && sliderId) {
    showFooter = false;
  }
  // Footer setup <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Functions >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

  const editTimesheet = useCallback(
    (timesheet) => {
      let timesheetId = timesheet.uid;

      if (inEditMode) {
        markItem(timesheet);
      } else {
        if (sliders) {
          // If sliders are on, add chosen primary id to session storage

          setSliderId(timesheetId);
        }

        history.push(`/timesheet/${timesheetId}`, { timesheet });
      }
    },
    [history, inEditMode, markItem, setSliderId, sliders],
  );

  async function mapView() {
    await toggleEditMode(document.body);
    let sheetsForMap = markedItems.map((primaryId) => {
      let lookingFor = timesheets.find((ts) => {
        return primaryId === ts.uid;
      });

      return lookingFor;
    });

    history.push("/tom-tom-map", {
      sheets: sheetsForMap,
    });
  }

  async function deleteTimesheets() {
    await transferTimesheets(markedItems, timesheetsRef, restoresRef, "timesheet");
    editMarkedItems([]);
    toggleEditMode(document.body);
  }

  async function clone(e) {
    let cloneType = e.target.dataset.clonetype || "all";

    await cloneTimesheets(markedItems, cloneType, timesheetsRef, timesheetsRef, "timesheet", vat);
    editMarkedItems([]);
    toggleEditMode(document.body);
  }

  async function convertToQuotes() {
    await transferTimesheets(
      markedItems,
      timesheetsRef,
      quotesRef,
      "quote",
      currentQuoteNumber,
      incCurrentPrimaryNumber,
    );
    editMarkedItems([]);
    toggleEditMode(document.body);
  }

  async function convertToJobsheets() {
    await transferTimesheets(
      markedItems,
      timesheetsRef,
      jobsheetsRef,
      "jobsheet",
      currentJobsheetNumber,
      incCurrentPrimaryNumber,
    );
    editMarkedItems([]);
    toggleEditMode(document.body);
  }

  const addTimesheet = async function (e) {
    let result = await addPrimary("timesheet", auth.user.uid);

    if (result?.success) {
      setSliderId(result?.uid);

      //addPrimary returns an object which lets you know if the operation failed or succeeded.
      //Use this to introduce useful error messgaes in the component.
    }
  };

  // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
  // Functions <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  if (loading) {
    return <Loading />;
  }

  return (
    <div ref={containerRef} className="primariesPage">
      <SearchSortFilterBar options={sortAndFilterOptions} setOptions={setSortAndFilterOptions} />

      <StatusFilters
        options={sortAndFilterOptions}
        setOptions={setSortAndFilterOptions}
        primaryType={"timesheets"}
      />

      <SliderWrapper>
        <>
          {/* Primary Data >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> */}
          {/* Shows Timesheets */}
          <div className={`primariesList ${inEditMode ? "dimSheets" : ""}`}>
            {sectionedTimesheets.map(({ title, data }) => {
              return (
                // <Suspense key={title} fallback={<div>isLoading...</div>}>
                <PrimarySection
                  key={title}
                  title={title}
                  data={data}
                  markItem={markItem}
                  editPrimary={editTimesheet}
                  isMarked={isMarked}
                  primarySectionType={"timesheet"}
                  markMonth={markMonth}
                  // editPrimary={markItem}
                />
                // </Suspense>
              );
            })}

            <div ref={endOfViewWindow}></div>
          </div>
          {/* Primary Data <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}
          {/* Global Search Data (Jobsheets & Quotes)>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> */}
          {/* Only show when searching: Jobsheets and Quotes */}
          {showGlobalSearch && (
            <div className={`primariesList ${inEditMode ? "hide" : ""}`}>
              <PrimarySection
                title={"Jobsheets & Quotes"}
                data={globalSearchData}
                markItem={markItem}
                markMonth={() => {}}
                editPrimary={(data) => {
                  history.push(`/${data.primaryType}/${data.uid}`, {
                    data,
                    preservePrimary: true,
                  });
                }}
                isMarked={() => {}}
                primarySectionType={"timesheet"}
              />
            </div>
          )}
          {/* Global Search Data <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}
        </>

        <SliderContainer primaryType="timesheet" />
      </SliderWrapper>

      {showFooter && (
        <Footer back={false} title="Timesheets" backTo={"/"}>
          <FooterBtn
            title={tooltips.edit}
            cb={() => toggleEditMode(document.body)}
            isActive={inEditMode}
          >
            <EditIcon style={{ fontSize: 25 }} />
          </FooterBtn>

          <FooterBtn title={tooltips.mapView} cb={mapView} show={showBin}>
            <LocationOnIcon style={{ fontSize: 30 }} />
          </FooterBtn>

          {showBin && <CloneOptions clone={clone} inEditMode={inEditMode} />}

          <FooterBtn title={tooltips.jobsheetConvert} cb={convertToJobsheets} show={showBin}>
            <RestoreIcon title="Convert to Jobsheets" style={{ fontSize: 28 }} />
            <span>J</span>
          </FooterBtn>

          <FooterBtn title={tooltips.quoteConvert} cb={convertToQuotes} show={showBin}>
            <RestoreIcon title="Convert to Quotes" style={{ fontSize: 28 }} />
            <span>Q</span>
          </FooterBtn>

          <FooterBtn title={tooltips.bin} cb={deleteTimesheets} show={showBin}>
            <DeleteOutlinedIcon style={{ fontSize: 30 }} />
          </FooterBtn>

          <FooterBtn title={tooltips.createTimesheet} cb={addTimesheet} show={!showBin}>
            <AddIcon style={{ fontSize: 30 }} />
          </FooterBtn>
        </Footer>
      )}
    </div>
  );
};

export default React.memo(Timesheets);
